import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SipService } from '../services/sip.service';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DtmfDialogComponent } from '../dtmf-dialog/dtmf-dialog.component';
import { EveCall } from '../models/eve-call';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ConferenceRoom,
  ConferenceService,
} from '../services/conference.service';
import { DialogInfoService } from '../services/dialog-info.service';
import { Dialog } from '../models/dialog';
import { InviterOptions } from 'sip.js';

@Component({
  selector: 'app-active-sessions',
  templateUrl: './active-sessions.component.html',
  styleUrls: ['./active-sessions.component.css']
})
export class ActiveSessionsComponent implements OnInit, OnDestroy {
  @Input() filteredActiveCalls: EveCall[] = [];
  @Input() incomingCalls: EveCall[] = [];
  @Input() activeDialogs: Dialog[] | null = null;
  @Input() incomingDialogs: Dialog[] | null = null;
  @Input() conferences: ConferenceRoom[] | null = null;

  private filteredActiveCallsSubscription?: Subscription;
  private incomingCallsSubscription?: Subscription;
  private conferenceSubscription?: Subscription;
  private dialogInfoSubscription?: Subscription;

  // Active calls from SIP service (calls with session state not Terminated)
 
  constructor(
    private sipService: SipService,
    private dialogInfoService: DialogInfoService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private conferenceService: ConferenceService
  ) {}

  ngOnInit() {
   
  }

  ngOnDestroy() {
    this.filteredActiveCallsSubscription?.unsubscribe();
    this.incomingCallsSubscription?.unsubscribe();
    this.conferenceSubscription?.unsubscribe();
    this.dialogInfoSubscription?.unsubscribe();
  }

 
  async acceptCall(call: EveCall) {
    // Accept incoming call via SIP service
    try {
      await this.holdAllUnHeldCalls();

      // find active conference and deactivate it
      const activeConference = this.conferenceService.getActiveConference();
      if (activeConference) {
        await this.conferenceService.deactivateCurrentConference();
      }

      await this.sipService.acceptCall(call);
    } catch (error: any) {
      const errorMessage = 'Failed to accept call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async rejectCall(call: EveCall) {
    // Reject incoming call via SIP service
    try {
      await this.sipService.rejectCall(call);
    } catch (error: any) {
      const errorMessage = 'Failed to reject call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async toggleHold(call: EveCall) {
    if (call.isOnHold) {
      try {
        // find active conference and deactivate it
        const activeConference = this.conferenceService.getActiveConference();
        if (activeConference) {
          await this.conferenceService.deactivateCurrentConference();
        }

        const activeCalls = this.sipService.getActiveCalls();
        // if find the call in active calls, update it to not on hold
        const unheldCall = activeCalls.find((c) => !c.isOnHold);
        if (unheldCall) {
          await unheldCall.holdCall();
        }
        await call.unholdCall();

        const updatedCalls = activeCalls.map((c) => {
          if (c === call) {
            return call;
          } else if (c === unheldCall) {
            return unheldCall;
          }
          return c;
        });
      } catch (error: any) {
        const errorMessage = 'Failed to unhold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    } else {
      try {
        await call.holdCall();
      } catch (error: any) {
        const errorMessage = 'Failed to hold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    }
  }

  async endCall(call: EveCall) {
    try {
      await call.endCall();
    } catch (error: any) {
      const errorMessage = 'Failed to end call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async parkCall(call: EveCall) {
    try {
      await this.sipService.transferCall(call, 'callPark@iameve.co.uk');
    } catch (error) {
      const errorMessage =
        'Failed to park call: ' +
        (error instanceof Error ? error.message : 'Unknown error');
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async pickupDialog(dialog: Dialog) {
    let options: InviterOptions = {
      extraHeaders: [
        `Replaces: ${dialog.eveCallId};to-tag=${dialog.localTag};from-tag=${dialog.remoteTag}`,
      ],
    };

    try {
      await this.sipService.makeCall(dialog.localDisplayName??"",dialog.localUri!, options);
    } catch (error: any) {
      const errorMessage = 'Failed to pickup dialog: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  toggleCallMute(call: EveCall): void {
    try {
      if (call.isMuted) {
        call.unmute();
      } else {
        call.mute();
      }
    } catch (error) {
      const errorMessage =
        'Failed to toggle mute: ' +
        (error instanceof Error ? error.message : 'Unknown error');
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async mergeIncomingToConference(call: EveCall) {
    const hasActiveConference = this.conferenceService.hasActiveConference();
    try {
      // If a conference already exists, just add; else create
      await this.conferenceService.mergeCallIntoConference(call);

      const activeCalls = this.sipService.getActiveCalls();
      // Update the conference status for all active calls
      activeCalls.forEach((c) => {
        if (c === call) {
          c.isInConference = true;
        }
      });
      this.sipService.setActiveCalls(activeCalls);
    } catch (e: any) {
      const errorMessage = hasActiveConference
        ? 'Failed to merge call into conference: ' + e.message
        : 'Failed to create conference: ' + e.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  openDtmfDialog(call: EveCall) {
    this.dialog.open(DtmfDialogComponent, {
      width: '250px',
      data: { call: call },
    });
  }

  hasActiveConference(): boolean {
    return this.conferenceService.hasActiveConference();
  }

  hasActiveCall(): boolean {
    const activeCall = this.sipService.getActiveCalls().find((c) => {
      return !c.isOnHold && !c.isInConference;
    });
    return !!activeCall;
  }

  private getAllUnHoldCalls(): EveCall[] | null {
    const activeCalls = this.sipService.getActiveCalls();
    if (!activeCalls || activeCalls.length === 0) return null;
    return activeCalls.filter(
      (c) => c.isOnHold === false && c.isInConference === false
    );
  }

  private async holdAllUnHeldCalls(): Promise<void[]> {
    const unheldCalls = this.getAllUnHoldCalls();
    if (!unheldCalls || unheldCalls.length === 0) return Promise.resolve([]);
    return await Promise.all(unheldCalls.map((c) => c.holdCall()));
  }
}

